import { PropsWithChildren } from "react";

import { css } from "@compiled/react";
import { FormattedMessage, defineMessage, useIntl } from "react-intl";

import { token } from "@atlaskit/tokens";

import Link from "@/components/wpl/link/src";
import { useLocalizedHref } from "@/libraries/web-framework";
import {
    TypographyStyles,
    ProductKeyTypes
} from "@/libraries/wpl/foundations/src";

import { CardIcon, TitleCard } from "./components/card";
import { MotionContainer } from "./components/motion-container";
import { RowGrid } from "./layouts/row-grid";
import { LinkColumn } from "./layouts/subnav-link-columns";
import { useCurrentNav } from "./nav-links-container";

type ResourceData = {
    id: string;
    title: ReturnType<typeof defineMessage>;
    content: ReturnType<typeof defineMessage>;
    href: string;
    icon?: CardIcon;
};

function useResourcesData(): Array<ResourceData> {
    return [
        {
            id: "resources.customer-support",
            title: defineMessage({
                id: "globalnavv2.subnav.resources.customer-support.title",
                defaultMessage: "Customer Support"
            }),
            content: defineMessage({
                id: "globalnavv2.subnav.resources.customer-support.content",
                defaultMessage: "Ask questions, report bugs & give us feedback"
            }),
            href: "//support.atlassian.com/"
        },
        {
            id: "resources.find-partners",
            title: defineMessage({
                id: "globalnavv2.subnav.resources.find-partners.title",
                defaultMessage: "Find Partners"
            }),
            content: defineMessage({
                id: "globalnavv2.subnav.resources.find-partners.content",
                defaultMessage:
                    "Consulting, training & product customization support"
            }),
            href: "//partnerdirectory.atlassian.com/"
        },
        {
            id: "resources.migration-program",
            title: defineMessage({
                id: "globalnavv2.subnav.resources.migration-program.title",
                defaultMessage: "Migration Program"
            }),
            content: defineMessage({
                id: "globalnavv2.subnav.resources.migration-program.content",
                defaultMessage:
                    "Step-by-step guidance for your Atlassian cloud migration"
            }),
            href: useLocalizedHref("/migration")
        },
        {
            id: "resources.university",
            title: defineMessage({
                id: "globalnavv2.subnav.resources.university.title",
                defaultMessage: "University"
            }),
            content: defineMessage({
                id: "globalnavv2.subnav.resources.university.content",
                defaultMessage:
                    "Learn and expand your skill set for all things Atlassian"
            }),
            href: "//university.atlassian.com/"
        }
    ];
}

function useResourcesLinks() {
    return {
        support: {
            id: "support",
            title: defineMessage({
                id: "globalnavv2.subnav.resources.links.support",
                defaultMessage: "Support"
            }),
            items: [
                {
                    id: "general-inquiries",
                    text: defineMessage({
                        id: "globalnavv2.subnav.resources.links.general-inquiries",
                        defaultMessage: "General Inquiries"
                    }),
                    href: useLocalizedHref("/company/contact/general-inquiries")
                },
                {
                    id: "technical-support",
                    text: defineMessage({
                        id: "globalnavv2.subnav.resources.links.technical-support",
                        defaultMessage: "Technical Support"
                    }),
                    href: "//support.atlassian.com/contact/"
                },
                {
                    id: "product-advice",
                    text: defineMessage({
                        id: "globalnavv2.subnav.resources.links.product-advice",
                        defaultMessage: "Product Advice"
                    }),
                    href: useLocalizedHref(
                        "/company/contact/product-evaluator-advice"
                    )
                },
                {
                    id: "pricing-billing",
                    text: defineMessage({
                        id: "globalnavv2.subnav.resources.links.pricing-billing",
                        defaultMessage: "Pricing and Billing"
                    }),
                    href: useLocalizedHref(
                        "/company/contact/purchasing-licensing"
                    )
                },
                {
                    id: "partner-support",
                    text: defineMessage({
                        id: "globalnavv2.subnav.resources.links.partner-support",
                        defaultMessage: "Partner Support"
                    }),
                    href: useLocalizedHref("/partners")
                },
                {
                    id: "developer-support",
                    text: defineMessage({
                        id: "globalnavv2.subnav.resources.links.developer-support",
                        defaultMessage: "Developer Support"
                    }),
                    href: "//developer.atlassian.com/"
                },
                {
                    id: "enterprise-support",
                    text: defineMessage({
                        id: "globalnavv2.subnav.resources.links.enterprise-support",
                        defaultMessage: "Enterprise Support"
                    }),
                    href: useLocalizedHref("/enterprise/services")
                },
                {
                    id: "purchasing-licensing",
                    text: defineMessage({
                        id: "globalnavv2.subnav.resources.links.purchasing-billing",
                        defaultMessage: "Purchasing and Licensing"
                    }),
                    href: useLocalizedHref("/licensing/purchase-licensing")
                }
            ]
        },
        learn: {
            id: "learn",
            title: defineMessage({
                id: "globalnavv2.subnav.resources.links.learn",
                defaultMessage: "Learn"
            }),
            items: [
                {
                    id: "project-management",
                    text: defineMessage({
                        id: "globalnavv2.subnav.resources.links.project-management",
                        defaultMessage: "Project Management"
                    }),
                    href: useLocalizedHref(
                        "/work-management/project-management"
                    )
                },
                {
                    id: "project-collaboration",
                    text: defineMessage({
                        id: "globalnavv2.subnav.resources.links.project-collaboration",
                        defaultMessage: "Project Collaboration"
                    }),
                    href: useLocalizedHref(
                        "/work-management/project-collaboration"
                    )
                },
                {
                    id: "agile",
                    text: defineMessage({
                        id: "globalnavv2.subnav.resources.links.agile",
                        defaultMessage: "Agile"
                    }),
                    href: useLocalizedHref("/agile")
                },
                {
                    id: "team-playbook",
                    text: defineMessage({
                        id: "globalnavv2.subnav.resources.links.team-playbook",
                        defaultMessage: "Team Playbook"
                    }),
                    href: useLocalizedHref("/team-playbook")
                },
                {
                    id: "community",
                    text: defineMessage({
                        id: "globalnavv2.subnav.resources.links.community",
                        defaultMessage: "Community"
                    }),
                    href: useLocalizedHref("/community")
                },
                {
                    id: "product-documentation",
                    text: defineMessage({
                        id: "globalnavv2.subnav.resources.links.product-documentation",
                        defaultMessage: "Product Documentation"
                    }),
                    href: "//confluence.atlassian.com/display/ALLDOC/Atlassian+Documentation"
                },
                {
                    id: "get-started",
                    text: defineMessage({
                        id: "globalnavv2.subnav.resources.links.get-started",
                        defaultMessage: "Get Started"
                    }),
                    href: useLocalizedHref("/get-started")
                }
            ]
        }
    } as const;
}

const ResourcesGrid = ({ children }: PropsWithChildren) => {
    return (
        <RowGrid
            numCols={4}
            rowGap={token("space.500")}
            columnGap={token("space.400")}
            paddingBlock={token("space.500")}
            paddingInline={token("space.500")}
        >
            {children}
        </RowGrid>
    );
};

export const ResourcesSubnav = ({
    productKey = "home"
}: {
    productKey?: ProductKeyTypes;
}) => {
    const intl = useIntl();

    const resourcesData = useResourcesData();
    const resourcesLinks = useResourcesLinks();
    const linkColumns = Object.values(resourcesLinks);

    const { direction } = useCurrentNav();

    return (
        <TypographyStyles>
            <MotionContainer direction={direction}>
                <ResourcesGrid>
                    {resourcesData.map(({ id, title, content, ...rest }) => (
                        <TitleCard
                            key={id}
                            title={intl.formatMessage(title)}
                            content={intl.formatMessage(content)}
                            productKey={productKey}
                            {...rest}
                        />
                    ))}
                </ResourcesGrid>
                <div css={css({ backgroundColor: "#F8F8F8" })}>
                    <RowGrid
                        numCols={2}
                        columnGap={token("space.400")}
                        paddingBlock={token("space.500")}
                        paddingInline={token("space.500")}
                    >
                        {linkColumns.map((column) => {
                            return (
                                <LinkColumn key={column.id}>
                                    <span
                                        css={css({
                                            textTransform: "uppercase",
                                            color: "#101214",
                                            fontSize: "0.75rem",
                                            fontFamily:
                                                "var(--bxp-charlie-text-font-family)",
                                            fontWeight: 700,
                                            lineHeight: "1rem"
                                        })}
                                    >
                                        <FormattedMessage {...column.title} />
                                    </span>
                                    <RowGrid
                                        numCols={2}
                                        columnGap={token("space.400")}
                                        numRows={4}
                                        rowHeight={token("space.500")}
                                    >
                                        {column.items.map((link) => {
                                            return (
                                                <div
                                                    key={link.id}
                                                    css={css({
                                                        a: {
                                                            fontWeight: 400,
                                                            fontSize:
                                                                "0.875rem",
                                                            lineHeight:
                                                                "1.5rem",
                                                            color: "#101214"
                                                        },

                                                        display: "flex",
                                                        alignItems: "end"
                                                    })}
                                                >
                                                    <Link
                                                        type="anchor"
                                                        href={link.href}
                                                        text={intl.formatMessage(
                                                            link.text
                                                        )}
                                                        ariaLabel={intl.formatMessage(
                                                            link.text
                                                        )}
                                                        dataEventProps={{
                                                            "data-event":
                                                                "clicked",
                                                            "data-event-component":
                                                                "linkButton",
                                                            "data-event-container":
                                                                "globalNavSubnav",
                                                            "data-product-key":
                                                                productKey
                                                        }}
                                                    />
                                                </div>
                                            );
                                        })}
                                    </RowGrid>
                                </LinkColumn>
                            );
                        })}
                    </RowGrid>
                </div>
            </MotionContainer>
        </TypographyStyles>
    );
};
